"use client";

import * as React from "react";
import { CheckIcon, ChevronDownIcon, SearchIcon } from "lucide-react";
import { Command as CommandPrimitive } from "cmdk";

import { cn } from "./utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/* ─── Types ──────────────────────────────────────────────────────── */

export interface ComboboxOption {
    value: string;
    label: string;
    /** Optional group key for grouped rendering. */
    group?: string;
    /** Disable this option. */
    disabled?: boolean;
}

interface ComboboxBaseProps {
    /** Options to display. */
    options: ComboboxOption[];
    /** Placeholder when no value is selected. */
    placeholder?: string;
    /** Placeholder inside the search input. */
    searchPlaceholder?: string;
    /** Text shown when search yields no results. */
    emptyText?: string;
    /** Additional className on the trigger button. */
    className?: string;
    /** Disabled state. */
    disabled?: boolean;
    /** Render a custom label for each option (defaults to option.label). */
    renderOption?: (option: ComboboxOption) => React.ReactNode;
}

/* ─── Single-select Combobox ─────────────────────────────────────── */

interface ComboboxSingleProps extends ComboboxBaseProps {
    value: string;
    onValueChange: (value: string) => void;
}

export function Combobox({
    options,
    value,
    onValueChange,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    emptyText = "No results.",
    className,
    disabled,
    renderOption,
}: ComboboxSingleProps) {
    const [open, setOpen] = React.useState(false);

    const selectedOption = options.find((o) => o.value === value);
    const groups = groupOptions(options);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-input-background px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
                        className,
                    )}
                >
                    <span className={cn("truncate", !selectedOption && "text-muted-foreground")}>
                        {selectedOption
                            ? (renderOption ? renderOption(selectedOption) : selectedOption.label)
                            : placeholder}
                    </span>
                    <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <CommandPrimitive className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
                    <div className="flex h-9 items-center gap-2 border-b px-3">
                        <SearchIcon className="size-4 shrink-0 opacity-50" />
                        <CommandPrimitive.Input
                            placeholder={searchPlaceholder}
                            className="placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                        />
                    </div>
                    <CommandPrimitive.List
                        onWheel={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                        className="max-h-[min(300px,var(--radix-popover-content-available-height,300px))] scroll-py-1 overflow-x-hidden overflow-y-auto p-1"
                    >
                        <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">
                            {emptyText}
                        </CommandPrimitive.Empty>
                        {groups.map(([groupLabel, groupOptions]) => (
                            <CommandPrimitive.Group
                                key={groupLabel}
                                heading={groupLabel || undefined}
                                className={cn(
                                    "text-foreground overflow-hidden [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
                                )}
                            >
                                {groupOptions.map((option) => (
                                    <CommandPrimitive.Item
                                        key={option.value}
                                        value={option.label}
                                        disabled={option.disabled}
                                        onSelect={() => {
                                            onValueChange(option.value);
                                            setOpen(false);
                                        }}
                                        className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                                    >
                                        {renderOption ? renderOption(option) : option.label}
                                        {option.value === value && (
                                            <CheckIcon className="absolute right-2 size-4" />
                                        )}
                                    </CommandPrimitive.Item>
                                ))}
                            </CommandPrimitive.Group>
                        ))}
                    </CommandPrimitive.List>
                </CommandPrimitive>
            </PopoverContent>
        </Popover>
    );
}

/* ─── Multi-select Combobox ──────────────────────────────────────── */

interface ComboboxMultiProps extends ComboboxBaseProps {
    value: Set<string>;
    onToggle: (value: string) => void;
    onClear: () => void;
    /** Label to show for "all selected" state. */
    allLabel?: string;
    /**
     * Accessible name for the trigger. ARIA's `combobox` role does not take its
     * name from content, so without this the control announces as an unnamed
     * combobox however clearly it is labelled on screen.
     */
    ariaLabel?: string;
}

export function ComboboxMulti({
    options,
    value,
    onToggle,
    onClear,
    placeholder: _placeholder = "Select…",
    allLabel = "All",
    searchPlaceholder = "Search…",
    emptyText = "No results.",
    className,
    disabled,
    renderOption,
    ariaLabel,
}: ComboboxMultiProps) {
    const [open, setOpen] = React.useState(false);

    // Counted against the list, not the set: a stored selection can still name
    // a value the list no longer offers — an auto-sync schedule keeps the room
    // kinds it was saved with, one the PMS has since dropped included — and
    // "3 / 6" over two ticked rows is a selection nobody can find.
    const selectedCount = options.filter((option) => value.has(option.value)).length;
    const areAllSelected = options.length > 0 && selectedCount === options.length;
    const triggerLabel =
        value.size > 0 && !areAllSelected
            ? `${selectedCount} / ${options.length}`
            : allLabel;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={ariaLabel}
                    disabled={disabled}
                    className={cn(
                        "border-input [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-input-background px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
                        className,
                    )}
                >
                    <span className={cn("truncate", (value.size === 0 || areAllSelected) && "text-muted-foreground")}>
                        {triggerLabel}
                    </span>
                    <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <CommandPrimitive className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
                    <div className="flex h-9 items-center gap-2 border-b px-3">
                        <SearchIcon className="size-4 shrink-0 opacity-50" />
                        <CommandPrimitive.Input
                            placeholder={searchPlaceholder}
                            className="placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                        />
                    </div>
                    <CommandPrimitive.List
                        onWheel={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                        className="max-h-[min(300px,var(--radix-popover-content-available-height,300px))] scroll-py-1 overflow-x-hidden overflow-y-auto p-1"
                    >
                        <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">
                            {emptyText}
                        </CommandPrimitive.Empty>
                        {/* "All" toggle */}
                        <CommandPrimitive.Item
                            value={allLabel}
                            onSelect={onClear}
                            className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none"
                        >
                            {allLabel}
                            {(value.size === 0 || areAllSelected) && (
                                <CheckIcon className="absolute right-2 size-4" />
                            )}
                        </CommandPrimitive.Item>
                        {options.map((option) => (
                            <CommandPrimitive.Item
                                key={option.value}
                                value={option.label}
                                disabled={option.disabled}
                                onSelect={() => onToggle(option.value)}
                                className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                            >
                                {renderOption ? renderOption(option) : option.label}
                                {value.has(option.value) && (
                                    <CheckIcon className="absolute right-2 size-4" />
                                )}
                            </CommandPrimitive.Item>
                        ))}
                    </CommandPrimitive.List>
                </CommandPrimitive>
            </PopoverContent>
        </Popover>
    );
}

/* ─── Helpers ────────────────────────────────────────────────────── */

/** Group options by their `group` key. Options without a group are gathered under "". */
function groupOptions(options: ComboboxOption[]): [string, ComboboxOption[]][] {
    const map = new Map<string, ComboboxOption[]>();
    for (const o of options) {
        const g = o.group ?? "";
        if (!map.has(g)) map.set(g, []);
        map.get(g)!.push(o);
    }
    return [...map.entries()];
}
